import type { Patient, Reference } from '@medplum/fhirtypes'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { documentFingerprint } from '@seen/core/models/document-references/fingerprint'
import { orderBy, uniq } from 'lodash-es'

export interface DuplicateFile {
  file: File
  document: ExtendedDocumentReference
}

export interface DuplicateCheckResult {
  fresh: File[]
  duplicates: DuplicateFile[]
  // v1 fingerprint of `fresh` as one document. Absent when nothing is fresh.
  freshFingerprint?: string
}

type DocumentQuery = ReturnType<
  typeof ExtendedDocumentReference.byParticipantFingerprint
>

interface FindDuplicateFilesOptions {
  search: (query: DocumentQuery) => Promise<ExtendedDocumentReference[]>
  participant: Reference<Patient>
}

// Matches against the fingerprint v1 stamps on documents, so a file is a
// duplicate of a single-file document and a whole multi-file selection is a
// duplicate of a document holding exactly those files. A file inside a larger
// multi-file document is not detected: v1 fingerprints the document, not its
// parts.
export async function findDuplicateFiles(
  files: readonly File[],
  { search, participant }: FindDuplicateFilesOptions
): Promise<DuplicateCheckResult> {
  const entries = await Promise.all(
    files.map(async (file) => {
      const bytes = await readBytes(file)
      return { file, bytes, fingerprint: await documentFingerprint([bytes]) }
    })
  )
  const selectionFingerprint =
    entries.length > 1
      ? await documentFingerprint(entries.map((entry) => entry.bytes))
      : undefined

  const matches = new Map<string, ExtendedDocumentReference>()
  await Promise.all(
    uniq([
      ...entries.map((entry) => entry.fingerprint),
      ...(selectionFingerprint ? [selectionFingerprint] : []),
    ]).map(async (fingerprint) => {
      const match = await findCanonicalMatch(search, participant, fingerprint)
      if (match) {
        matches.set(fingerprint, match)
      }
    })
  )

  const selectionMatch = selectionFingerprint
    ? matches.get(selectionFingerprint)
    : undefined
  if (selectionMatch) {
    return {
      fresh: [],
      duplicates: files.map((file) => ({ file, document: selectionMatch })),
    }
  }

  const duplicates: DuplicateFile[] = []
  const freshEntries: typeof entries = []
  for (const entry of entries) {
    const document = matches.get(entry.fingerprint)
    if (document) {
      duplicates.push({ file: entry.file, document })
    } else {
      freshEntries.push(entry)
    }
  }

  const freshFingerprint =
    freshEntries.length > 0
      ? await documentFingerprint(freshEntries.map((entry) => entry.bytes))
      : undefined

  // The files left after skipping single-file duplicates become one document,
  // which may itself already be on the chart.
  const remainingMatch =
    freshEntries.length > 1 && freshFingerprint !== selectionFingerprint
      ? await findCanonicalMatch(search, participant, freshFingerprint ?? '')
      : undefined
  if (remainingMatch) {
    return {
      fresh: [],
      duplicates: entries.map((entry) => ({
        file: entry.file,
        document: matches.get(entry.fingerprint) ?? remainingMatch,
      })),
    }
  }

  return {
    fresh: freshEntries.map((entry) => entry.file),
    duplicates,
    freshFingerprint,
  }
}

// A failed lookup lets the upload through: the check is best-effort, and a
// search outage must not stop clinical documents reaching the chart.
async function findCanonicalMatch(
  search: FindDuplicateFilesOptions['search'],
  participant: Reference<Patient>,
  fingerprint: string
): Promise<ExtendedDocumentReference | undefined> {
  try {
    const documents = await search(
      ExtendedDocumentReference.byParticipantFingerprint(
        participant,
        fingerprint
      )
    )
    return orderBy(
      documents,
      [(doc) => doc.dateStringWithFallback ?? '', (doc) => doc.id],
      ['asc', 'asc']
    )[0]
  } catch {
    return undefined
  }
}

// FileReader rather than Blob.arrayBuffer, which the jsdom test environment
// lacks.
function readBytes(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read file'))
    reader.readAsArrayBuffer(blob)
  })
}
